/**
 * Companion series: a pen's rolling average or training-bucket view, stored as
 * an extra chart column under a prefixed sensor id and drawn against the parent
 * pen's scale and colour.
 *
 * Companions stay out of `allSensorsToRender`, which drives axes, the ruler,
 * annotations and save state — all of which the parent pen already owns.
 */

export const SMOOTHED_COLUMN_PREFIX = 'smoothed__'
export const BUCKET_COLUMN_PREFIX = 'bucket__'

export type PenCompanionKind = 'smoothed' | 'bucket'

export const smoothedColumnId = (sensorId: string): string =>
  `${SMOOTHED_COLUMN_PREFIX}${sensorId}`

export const bucketColumnId = (sensorId: string): string =>
  `${BUCKET_COLUMN_PREFIX}${sensorId}`

export interface PenCompanion {
  kind: PenCompanionKind
  /** Row key holding the companion's values. */
  columnId: string
  /** The pen this companion belongs to, whose scale and colour it borrows. */
  parentSensorId: string
}

const PREFIXES: Array<{ kind: PenCompanionKind; prefix: string }> = [
  { kind: 'smoothed', prefix: SMOOTHED_COLUMN_PREFIX },
  { kind: 'bucket', prefix: BUCKET_COLUMN_PREFIX },
]

const LABELS: Record<PenCompanionKind, string> = {
  smoothed: 'rolling',
  bucket: 'buckets',
}

export const parsePenCompanionId = (columnId: string): PenCompanion | null => {
  for (const { kind, prefix } of PREFIXES) {
    if (columnId.startsWith(prefix)) {
      return {
        kind,
        columnId,
        parentSensorId: columnId.slice(prefix.length),
      }
    }
  }
  return null
}

/** Suffix appended to the parent pen's name in the legend and tooltip. */
export const penCompanionLabel = (kind: PenCompanionKind): string =>
  LABELS[kind]

/** One companion per enabled transform whose pen is on the chart. */
export const collectPenCompanions = ({
  penIds,
  smoothedSensorIds,
  bucketedSensorIds,
}: {
  penIds: Array<{ id: string }>
  smoothedSensorIds: string[]
  bucketedSensorIds: string[]
}): PenCompanion[] => {
  const pens = new Set(penIds.map((pen) => pen.id))

  const forKind = (
    kind: PenCompanionKind,
    sensorIds: string[],
    toColumnId: (sensorId: string) => string
  ): PenCompanion[] =>
    sensorIds
      .filter((sensorId) => pens.has(sensorId))
      .map((sensorId) => ({
        kind,
        columnId: toColumnId(sensorId),
        parentSensorId: sensorId,
      }))

  return [
    ...forKind('smoothed', smoothedSensorIds, smoothedColumnId),
    ...forKind('bucket', bucketedSensorIds, bucketColumnId),
  ]
}
